import React, { useState, useEffect, useRef } from "react";

// ── Types ──────────────────────────────────────────────────────────────────
interface Template {
  id: number;
  icon: string;
  title: string;
  cat: string;
  tagClass: string;
  tagLabel: string;
  code: string;
}

// ── Data ───────────────────────────────────────────────────────────────────
const TEMPLATES: Template[] = [
  {
    id: 1,
    icon: "📱",
    title: "Flutter Widget Tree (Basic App)",
    cat: "widget",
    tagClass: "tag-widget",
    tagLabel: "Widget & UI",
    code: `graph TD
    A["MaterialApp"] --> B["Scaffold"]
    B --> C["AppBar"]
    B --> D["body: Column"]
    B --> E["floatingActionButton: FAB"]
    C --> C1["title: Text('My App')"]
    C --> C2["actions: IconButton"]
    D --> D1["Text('Hello Flutter')"]
    D --> D2["SizedBox(height: 16)"]
    D --> D3["ElevatedButton"]
    D --> D4["Image.network(url)"]
    E --> E1["Icon(Icons.add)"]

    style A fill:#0553B1,color:#fff
    style B fill:#1976D2,color:#fff
    style C fill:#42A5F5,color:#fff
    style D fill:#42A5F5,color:#fff`,
  },
  {
    id: 2,
    icon: "🏗️",
    title: "BLoC Architecture Pattern",
    cat: "architecture",
    tagClass: "tag-arch",
    tagLabel: "Architecture",
    code: `graph LR
    UI["UI Widget\\n(StatelessWidget)"]
    BLOC["BLoC\\n(Business Logic)"]
    REPO["Repository"]
    DS_R["Remote DataSource\\n(API/HTTP)"]
    DS_L["Local DataSource\\n(SQLite/Hive)"]

    UI -->|"add(Event)"| BLOC
    BLOC -->|"emit(State)"| UI
    BLOC -->|"fetch()"| REPO
    REPO --> DS_R
    REPO --> DS_L
    DS_R -->|"data"| REPO
    DS_L -->|"data"| REPO
    REPO -->|"Entity"| BLOC

    style UI fill:#E3F2FD,stroke:#0553B1
    style BLOC fill:#EDE7F6,stroke:#6750A4
    style REPO fill:#E8F5E9,stroke:#2E7D32
    style DS_R fill:#FFF3E0,stroke:#E65100
    style DS_L fill:#FFF3E0,stroke:#E65100`,
  },
  {
    id: 3,
    icon: "🧱",
    title: "Clean Architecture — Layer Diagram",
    cat: "architecture",
    tagClass: "tag-arch",
    tagLabel: "Architecture",
    code: `graph TD
    subgraph PRES["Presentation Layer"]
        P1["Pages / Screens"]
        P2["Widgets"]
        P3["BLoC / Cubit"]
    end

    subgraph DOMAIN["Domain Layer (Core)"]
        D1["Entities (Pure Dart classes)"]
        D2["Use Cases (Business Logic)"]
        D3["Repository Interfaces (abstract)"]
    end

    subgraph DATA["Data Layer"]
        DA1["Repository Implementations"]
        DA2["Remote DataSource (Dio)"]
        DA3["Local DataSource (Hive)"]
        DA4["Models (JSON to Entity)"]
    end

    PRES -->|"calls"| DOMAIN
    DOMAIN -->|"defines"| DATA
    PRES -.->|"NO direct access"| DATA

    style PRES fill:#E3F2FD,stroke:#1565C0
    style DOMAIN fill:#EDE7F6,stroke:#4527A0
    style DATA fill:#E8F5E9,stroke:#1B5E20`,
  },
  {
    id: 4,
    icon: "🔄",
    title: "BLoC State Flow",
    cat: "state",
    tagClass: "tag-state",
    tagLabel: "State Management",
    code: `stateDiagram-v2
    [*] --> InitialState
    InitialState --> LoadingState : add(FetchDataEvent)
    LoadingState --> LoadedState : data received
    LoadingState --> ErrorState : API fails
    ErrorState --> LoadingState : add(RetryEvent)
    LoadedState --> LoadingState : add(RefreshEvent)
    LoadedState --> [*]

    note right of LoadingState
        emit(LoadingState())
        await repository.getData()
    end note

    note right of LoadedState
        emit(LoadedState(data))
    end note`,
  },
  {
    id: 5,
    icon: "🧭",
    title: "App Navigation Flow (Go Router)",
    cat: "navigation",
    tagClass: "tag-nav",
    tagLabel: "Navigation",
    code: `graph TD
    SPLASH["SplashScreen /splash"]
    AUTH["AuthScreen /auth"]
    LOGIN["LoginScreen /login"]
    REGISTER["RegisterScreen /register"]
    HOME["HomeScreen /home"]
    DETAIL["DetailScreen /detail/:id"]
    PROFILE["ProfileScreen /profile"]
    SETTINGS["SettingsScreen /settings"]

    SPLASH -->|"isLoggedIn?"| HOME
    SPLASH -->|"not logged in"| AUTH
    AUTH --> LOGIN
    AUTH --> REGISTER
    LOGIN -->|"success"| HOME
    REGISTER -->|"success"| HOME
    HOME --> DETAIL
    HOME --> PROFILE
    HOME --> SETTINGS

    style SPLASH fill:#FFF9C4,stroke:#F9A825
    style HOME fill:#E8F5E9,stroke:#2E7D32
    style AUTH fill:#FCE4EC,stroke:#C62828`,
  },
  {
    id: 6,
    icon: "📊",
    title: "Provider State Management",
    cat: "state",
    tagClass: "tag-state",
    tagLabel: "State Management",
    code: `graph LR
    MP["MultiProvider\\n(Root Widget)"]
    CP1["ChangeNotifierProvider\\nUserProvider"]
    CP2["ChangeNotifierProvider\\nThemeProvider"]
    CP3["ChangeNotifierProvider\\nCartProvider"]

    MP --> CP1
    MP --> CP2
    MP --> CP3

    CP1 -->|"context.watch()"| W1["ProfileWidget"]
    CP1 -->|"context.read()"| W2["SettingsWidget"]
    CP2 -->|"context.watch()"| W3["ThemeWrapper"]
    CP3 -->|"context.watch()"| W4["CartBadge"]
    CP3 -->|"context.watch()"| W5["CheckoutPage"]

    style MP fill:#0553B1,color:#fff
    style CP1 fill:#42A5F5,color:#fff
    style CP2 fill:#42A5F5,color:#fff
    style CP3 fill:#42A5F5,color:#fff`,
  },
  {
    id: 7,
    icon: "⚡",
    title: "Riverpod Architecture",
    cat: "state",
    tagClass: "tag-state",
    tagLabel: "State Management",
    code: `graph TD
    subgraph PROVIDERS["Riverpod Providers"]
        P1["StateNotifierProvider\\nuserProvider"]
        P2["FutureProvider\\npostsProvider"]
        P3["StreamProvider\\nmessagesProvider"]
        P4["Provider\\ndioProvider"]
    end

    P4 -->|"injected into"| P2
    P4 -->|"injected into"| P3
    P1 -->|"ref.watch"| UI1["UserProfile Widget"]
    P2 -->|"when(loading,error,data)"| UI2["PostsList Widget"]
    P3 -->|"when(loading,error,data)"| UI3["ChatScreen Widget"]

    style P1 fill:#E8F5E9,stroke:#2E7D32
    style P2 fill:#FFF3E0,stroke:#E65100
    style P3 fill:#FCE4EC,stroke:#C62828
    style P4 fill:#F3E5F5,stroke:#6A1B9A`,
  },
  {
    id: 8,
    icon: "🗺️",
    title: "GetX Pattern",
    cat: "state",
    tagClass: "tag-state",
    tagLabel: "State Management",
    code: `graph LR
    BINDING["Bindings\\n(HomeBinding)"]
    CTRL["GetXController\\nHomeController"]
    VIEW["GetView\\nHomeScreen"]
    REPO["Repository"]
    API["API Service"]

    BINDING -->|"Get.lazyPut()"| CTRL
    VIEW -->|"Get.find()"| CTRL
    CTRL -->|"rx variables .obs"| VIEW
    CTRL -->|"calls"| REPO
    REPO -->|"calls"| API
    API -->|"Response"| REPO
    REPO -->|"Entity"| CTRL

    style BINDING fill:#FFF9C4,stroke:#F57F17
    style CTRL fill:#E8EAF6,stroke:#283593
    style VIEW fill:#E3F2FD,stroke:#0D47A1`,
  },
  {
    id: 9,
    icon: "🔁",
    title: "Flutter Widget Lifecycle",
    cat: "widget",
    tagClass: "tag-widget",
    tagLabel: "Widget & UI",
    code: `stateDiagram-v2
    [*] --> createState : StatefulWidget created
    createState --> initState : State object created
    initState --> didChangeDependencies
    didChangeDependencies --> build : First render
    build --> Active

    Active --> build : setState() called
    Active --> deactivate : removed from tree
    deactivate --> dispose : permanently removed
    dispose --> [*]

    Active --> didUpdateWidget : parent rebuilds
    didUpdateWidget --> build

    note right of initState
        API calls here
        Controller init
        Animations init
    end note

    note right of dispose
        Cancel subscriptions
        Dispose controllers
        Close streams
    end note`,
  },
  {
    id: 10,
    icon: "📐",
    title: "MVVM Pattern (Flutter)",
    cat: "architecture",
    tagClass: "tag-arch",
    tagLabel: "Architecture",
    code: `graph LR
    subgraph VIEW["View Layer"]
        V1["Screen Widget"]
        V2["Custom Widgets"]
    end

    subgraph VIEWMODEL["ViewModel Layer"]
        VM["ChangeNotifier ViewModel"]
        OBS["Observable State notifyListeners"]
    end

    subgraph MODEL["Model Layer"]
        M1["Repository"]
        M2["DataSource"]
        M3["Entity/Model"]
    end

    V1 -->|"calls methods"| VM
    VM -->|"notifyListeners()"| V1
    VM --> OBS
    OBS -->|"listened by"| V2
    VM -->|"async calls"| M1
    M1 --> M2
    M2 -->|"data"| M3
    M3 -->|"mapped"| VM

    style VIEW fill:#E3F2FD,stroke:#1565C0
    style VIEWMODEL fill:#EDE7F6,stroke:#4527A0
    style MODEL fill:#E8F5E9,stroke:#1B5E20`,
  },
  {
    id: 11,
    icon: "📱",
    title: "Flutter Full Stack Architecture",
    cat: "architecture",
    tagClass: "tag-arch",
    tagLabel: "Architecture",
    code: `graph TD
    subgraph FLUTTER["Flutter App"]
        UI["UI Layer Widgets/Screens"]
        BL["Business Logic BLoC/Provider/Riverpod"]
        RL["Repository Layer"]
    end

    subgraph BACKEND["Backend"]
        API["REST API Node/Django/Laravel"]
        DB_R["Database PostgreSQL/MySQL"]
        CACHE["Cache Redis"]
        STORAGE["Storage S3/Firebase"]
    end

    subgraph LOCAL["Local Storage"]
        HIVE["Hive NoSQL"]
        SQLITE["SQLite Relational"]
        SP["SharedPreferences"]
    end

    UI --> BL
    BL --> RL
    RL -->|"HTTP/Dio"| API
    RL --> HIVE
    RL --> SQLITE
    RL --> SP
    API --> DB_R
    API --> CACHE
    API --> STORAGE

    style FLUTTER fill:#E3F2FD,stroke:#0553B1
    style BACKEND fill:#E8F5E9,stroke:#2E7D32
    style LOCAL fill:#FFF3E0,stroke:#E65100`,
  },
  {
    id: 12,
    icon: "🎨",
    title: "Material 3 Color System",
    cat: "design",
    tagClass: "tag-design",
    tagLabel: "Design",
    code: `graph TD
    SEED["Seed Color #6750A4"]

    SEED --> PRIMARY["Primary #6750A4"]
    SEED --> SECONDARY["Secondary #625B71"]
    SEED --> TERTIARY["Tertiary #7D5260"]
    SEED --> ERROR["Error #B3261E"]
    SEED --> NEUTRAL["Neutral"]

    PRIMARY --> PC["PrimaryContainer #EADDFF"]
    SECONDARY --> SC["SecondaryContainer #E8DEF8"]
    TERTIARY --> TC["TertiaryContainer #FFD8E4"]
    ERROR --> EC["ErrorContainer #F9DEDC"]
    NEUTRAL --> SURFACE["Surface #FFFBFE"]
    NEUTRAL --> OUTLINE["Outline #79747E"]

    style SEED fill:#6750A4,color:#fff
    style PRIMARY fill:#6750A4,color:#fff
    style SECONDARY fill:#625B71,color:#fff
    style ERROR fill:#B3261E,color:#fff`,
  },
  {
    id: 13,
    icon: "🔗",
    title: "Dio HTTP Client Setup",
    cat: "data",
    tagClass: "tag-data",
    tagLabel: "Data & API",
    code: `graph LR
    APP["main.dart Dio setup"]
    DIO["Dio Instance + BaseOptions"]
    INT1["LoggingInterceptor debug logs"]
    INT2["AuthInterceptor Bearer token"]
    INT3["ErrorInterceptor 401 to refresh"]

    APP --> DIO
    DIO --> INT1
    DIO --> INT2
    DIO --> INT3

    INT2 -->|"inject header"| REQ["HTTP Request"]
    INT3 -->|"401 detected"| REFRESH["Refresh Token"]
    REFRESH -->|"new token"| INT2
    REQ -->|"Response"| INT1

    style DIO fill:#0553B1,color:#fff
    style INT1 fill:#E8F5E9,stroke:#2E7D32
    style INT2 fill:#FFF3E0,stroke:#E65100
    style INT3 fill:#FCE4EC,stroke:#C62828`,
  },
  {
    id: 14,
    icon: "📦",
    title: "Flutter Project Folder Structure",
    cat: "architecture",
    tagClass: "tag-arch",
    tagLabel: "Architecture",
    code: `graph TD
    ROOT["lib/"]
    ROOT --> CORE["core/"]
    ROOT --> FEATURES["features/"]
    ROOT --> SHARED["shared/"]

    CORE --> C1["error/ Failures, Exceptions"]
    CORE --> C2["network/ Dio, API client"]
    CORE --> C3["utils/ Extensions, Helpers"]
    CORE --> C4["theme/ AppTheme, Colors"]
    CORE --> C5["constants/ AppStrings, Routes"]

    FEATURES --> F1["auth/"]
    FEATURES --> F2["home/"]
    FEATURES --> F3["profile/"]

    F1 --> FA["data/ models, datasource, repo_impl"]
    F1 --> FB["domain/ entities, usecases, repo"]
    F1 --> FC["presentation/ bloc, pages, widgets"]

    SHARED --> S1["widgets/ common UI"]
    SHARED --> S2["extensions/ BuildContext, String"]

    style ROOT fill:#0553B1,color:#fff
    style CORE fill:#E3F2FD,stroke:#1565C0
    style FEATURES fill:#EDE7F6,stroke:#4527A0
    style SHARED fill:#E8F5E9,stroke:#1B5E20`,
  },
  {
    id: 15,
    icon: "🔐",
    title: "Authentication Flow (Sequence)",
    cat: "flow",
    tagClass: "tag-flow",
    tagLabel: "App Flow",
    code: `sequenceDiagram
    participant User
    participant LoginPage
    participant AuthBloc
    participant AuthRepo
    participant API
    participant SecureStorage

    User->>LoginPage: Enter email & password
    LoginPage->>AuthBloc: add(LoginEvent)
    AuthBloc->>AuthBloc: emit(AuthLoading)
    AuthBloc->>AuthRepo: login(email, pass)
    AuthRepo->>API: POST /auth/login
    API-->>AuthRepo: token + user data
    AuthRepo->>SecureStorage: save tokens
    AuthRepo-->>AuthBloc: UserEntity
    AuthBloc->>AuthBloc: emit(AuthAuthenticated)
    AuthBloc-->>LoginPage: navigate to HomeScreen`,
  },
  {
    id: 16,
    icon: "🛒",
    title: "E-Commerce App Flow",
    cat: "flow",
    tagClass: "tag-flow",
    tagLabel: "App Flow",
    code: `graph TD
    SPLASH["Splash Screen"]
    HOME["Home Screen Product Grid"]
    SEARCH["Search Screen"]
    DETAIL["Product Detail Images, Price, Reviews"]
    CART["Cart Screen"]
    CHECKOUT["Checkout Screen"]
    PAYMENT["Payment Screen"]
    SUCCESS["Order Success"]
    PROFILE["Profile Screen"]
    ORDERS["My Orders"]

    SPLASH --> HOME
    HOME --> SEARCH
    HOME --> DETAIL
    HOME --> PROFILE
    SEARCH --> DETAIL
    DETAIL -->|"Add to Cart"| CART
    CART -->|"Proceed"| CHECKOUT
    CHECKOUT -->|"Pay"| PAYMENT
    PAYMENT -->|"Success"| SUCCESS
    SUCCESS --> HOME
    PROFILE --> ORDERS

    style SPLASH fill:#FFF9C4,stroke:#F9A825
    style SUCCESS fill:#E8F5E9,stroke:#2E7D32
    style PAYMENT fill:#FCE4EC,stroke:#C62828`,
  },
  {
    id: 17,
    icon: "🔔",
    title: "Push Notification Flow (FCM)",
    cat: "flow",
    tagClass: "tag-flow",
    tagLabel: "App Flow",
    code: `sequenceDiagram
    participant App
    participant FCM as Firebase FCM
    participant Server
    participant User

    App->>FCM: Request FCM Token
    FCM-->>App: Device Token
    App->>Server: Register token POST /device-token

    Note over Server,FCM: Sending notification
    Server->>FCM: Send message token + payload
    FCM->>App: Deliver notification
    App->>User: Show notification

    User->>App: Tap notification
    App->>App: onMessageOpenedApp()
    App->>App: Navigate to specific screen`,
  },
  {
    id: 18,
    icon: "🗄️",
    title: "Hive Local Database Architecture",
    cat: "data",
    tagClass: "tag-data",
    tagLabel: "Data & API",
    code: `graph LR
    APP["main.dart"]
    INIT["Hive.init() Hive.initFlutter()"]
    REG["Register Adapters Hive.registerAdapter()"]
    BOX["Open Boxes Hive.openBox()"]

    subgraph BOXES["Hive Boxes"]
        B1["usersBox UserModel"]
        B2["productsBox ProductModel"]
        B3["settingsBox AppSettings"]
    end

    APP --> INIT --> REG --> BOX
    BOX --> B1
    BOX --> B2
    BOX --> B3

    B1 -->|"CRUD"| REPO1["UserLocalDataSource"]
    B2 -->|"CRUD"| REPO2["ProductLocalDataSource"]
    B3 -->|"get/set"| REPO3["SettingsDataSource"]

    style APP fill:#0553B1,color:#fff
    style B1 fill:#FFF3E0,stroke:#E65100
    style B2 fill:#FFF3E0,stroke:#E65100
    style B3 fill:#FFF3E0,stroke:#E65100`,
  },
  {
    id: 19,
    icon: "🧪",
    title: "Flutter Testing Pyramid",
    cat: "test",
    tagClass: "tag-test",
    tagLabel: "Testing",
    code: `graph TD
    subgraph E2E["E2E Tests Few - integration_test/"]
        E1["Full app flow tests"]
        E2["flutter drive"]
    end

    subgraph WIDGET["Widget Tests Some - test/widget/"]
        W1["Widget render tests"]
        W2["Interaction tests"]
        W3["Golden tests"]
    end

    subgraph UNIT["Unit Tests Many - test/unit/"]
        U1["BLoC / Cubit tests"]
        U2["Use Case tests"]
        U3["Repository tests"]
        U4["Model/Entity tests"]
    end

    style E2E fill:#FCE4EC,stroke:#C62828
    style WIDGET fill:#FFF3E0,stroke:#E65100
    style UNIT fill:#E8F5E9,stroke:#2E7D32`,
  },
  {
    id: 20,
    icon: "🌐",
    title: "Dio + Repository Pattern (Full)",
    cat: "data",
    tagClass: "tag-data",
    tagLabel: "Data & API",
    code: `graph TD
    subgraph REMOTE["Remote DataSource"]
        DS["ProductRemoteDataSource"]
        DIO["Dio Client"]
        DS --> DIO
    end

    subgraph MODELS["Data Models"]
        PM["ProductModel fromJson / toJson"]
    end

    subgraph REPO["Repository"]
        ABS["ProductRepository abstract"]
        IMPL["ProductRepositoryImpl"]
    end

    subgraph DOMAIN["Domain"]
        ENTITY["Product Entity pure Dart"]
        UC["GetProductsUseCase call()"]
    end

    DIO -->|"JSON Response"| PM
    PM -->|"toEntity()"| ENTITY
    DS --> IMPL
    ABS -.->|"implements"| IMPL
    IMPL -->|"returns Either"| UC
    UC -->|"Product list"| BL["BLoC / ViewModel"]

    style REMOTE fill:#E3F2FD,stroke:#1565C0
    style MODELS fill:#FFF3E0,stroke:#E65100
    style REPO fill:#EDE7F6,stroke:#4527A0
    style DOMAIN fill:#E8F5E9,stroke:#1B5E20`,
  },
  {
    id: 21,
    icon: "🌱",
    title: "App Lifecycle States",
    cat: "flow",
    tagClass: "tag-flow",
    tagLabel: "App Flow",
    code: `stateDiagram-v2
    [*] --> detached
    detached --> inactive : App Launched
    inactive --> resumed : App in Foreground
    resumed --> inactive : App goes to Background
    inactive --> paused : App is Backgrounded
    paused --> inactive : App brought to Foreground
    paused --> detached : App Killed/Terminated
    
    note right of resumed: User interacts here
    note left of paused: Save data, stop timers`,
  },
  {
    id: 22,
    icon: "🧱",
    title: "Stateless vs Stateful",
    cat: "widget",
    tagClass: "tag-widget",
    tagLabel: "Widget & UI",
    code: `graph LR
    subgraph STATELESS["StatelessWidget"]
        S1["Build Method"] --> S2["Immutable UI"]
    end
    
    subgraph STATEFUL["StatefulWidget"]
        SF1["Widget Class"] --> SF2["createState()"]
        SF2 --> SF3["State Object"]
        SF3 -->|"setState()"| SF4["Build Method"]
        SF4 --> SF5["Mutable UI"]
    end
    
    style STATELESS fill:#E8F5E9,stroke:#2E7D32
    style STATEFUL fill:#E3F2FD,stroke:#1565C0`,
  },
  {
    id: 23,
    icon: "🧭",
    title: "Basic Navigation (Push/Pop)",
    cat: "navigation",
    tagClass: "tag-navigation",
    tagLabel: "Navigation",
    code: `graph TD
    A["Home Screen"] -->|"Navigator.push()"| B["Details Screen"]
    B -->|"Navigator.pop()"| A
    A -->|"Navigator.pushReplacement()"| C["Login Screen"]
    
    subgraph STACK["Navigation Stack"]
        S1["[Home Screen]"]
        S2["[Home Screen, Details Screen]"]
    end
    
    A -.->|"Stack state"| S1
    B -.->|"Stack state"| S2`,
  },
  {
    id: 24,
    icon: "🔄",
    title: "setState Workflow",
    cat: "state",
    tagClass: "tag-state",
    tagLabel: "State Management",
    code: `sequenceDiagram
    participant User
    participant UI as Widget Build
    participant State as State Object
    
    User->>UI: Taps Button
    UI->>State: triggers callback
    State->>State: Updates variable (e.g. counter++)
    State->>State: calls setState()
    State->>UI: marks widget as dirty
    UI->>UI: rebuilds with new variable
    UI-->>User: Shows updated UI`,
  },
  {
    id: 25,
    icon: "⏳",
    title: "Future vs Stream",
    cat: "data",
    tagClass: "tag-data",
    tagLabel: "Data & API",
    code: `graph LR
    subgraph FUTURE["Future (Single Value)"]
        F1["API Call"] -.->|"Wait..."| F2["Data / Error"]
    end
    
    subgraph STREAM["Stream (Multiple Values)"]
        S1["Listen"] -.->|"yield 1"| S2["Event 1"]
        S1 -.->|"yield 2"| S3["Event 2"]
        S1 -.->|"yield 3"| S4["Event N..."]
    end
    
    style FUTURE fill:#FFF3E0,stroke:#E65100
    style STREAM fill:#E3F2FD,stroke:#1565C0`,
  },
  {
    id: 26,
    icon: "📏",
    title: "Flex Layout Model",
    cat: "design",
    tagClass: "tag-design",
    tagLabel: "Design",
    code: `graph TD
    subgraph ROW["Row (Horizontal)"]
        R1["MainAxis: Horizontal"]
        R2["CrossAxis: Vertical"]
    end
    
    subgraph COLUMN["Column (Vertical)"]
        C1["MainAxis: Vertical"]
        C2["CrossAxis: Horizontal"]
    end
    
    ROW -->|"Expanded()"| FLX1["Takes remaining space"]
    COLUMN -->|"Flexible()"| FLX2["Takes needed space up to max"]
    
    style ROW fill:#FCE4EC,stroke:#C62828
    style COLUMN fill:#E8F5E9,stroke:#2E7D32`,
  },
  {
    id: 27,
    icon: "🧱",
    title: "BLoC Pattern Architecture",
    cat: "architecture",
    tagClass: "tag-architecture",
    tagLabel: "Architecture",
    code: `graph TD
    UI["UI (Flutter)"] -->|"Adds Event"| BLOC["BLoC"]
    BLOC -->|"Requests Data"| REPO["Repository"]
    REPO -->|"Returns Data"| BLOC
    BLOC -->|"Emits State"| UI
    
    subgraph BLOC_INTERNAL["Inside BLoC"]
        E["Event"] --> MAP["mapEventToState / on<Event>"]
        MAP --> S["State"]
    end
    
    style UI fill:#E3F2FD,stroke:#1565C0
    style BLOC fill:#FFF3E0,stroke:#E65100
    style REPO fill:#E8F5E9,stroke:#2E7D32`,
  },
  {
    id: 28,
    icon: "💧",
    title: "Provider / Riverpod Flow",
    cat: "state",
    tagClass: "tag-state",
    tagLabel: "State Management",
    code: `graph TD
    P["Provider / Notifier"] -->|"Exposes State"| ROOT["App Root / Scope"]
    ROOT --> W1["Widget A (Consumer)"]
    ROOT --> W2["Widget B (Consumer)"]
    
    W1 -->|"Calls method"| P
    P -->|"Updates & notifyListeners()"| W1
    P -->|"Updates & notifyListeners()"| W2
    
    style P fill:#EDE7F6,stroke:#4527A0
    style ROOT fill:#E3F2FD,stroke:#1565C0`,
  },
  {
    id: 29,
    icon: "🌐",
    title: "Network Request Flow",
    cat: "data",
    tagClass: "tag-data",
    tagLabel: "Data & API",
    code: `sequenceDiagram
    participant UI
    participant Repo
    participant HTTP as Dio/http
    participant Server
    
    UI->>Repo: fetchUserData()
    Repo->>HTTP: GET /users/1
    HTTP->>Server: HTTP Request
    Server-->>HTTP: 200 OK (JSON)
    HTTP-->>Repo: Response Data
    Repo->>Repo: JSON -> UserModel
    Repo-->>UI: Returns UserModel
    UI->>UI: setState() / emit(State)`,
  },
  {
    id: 30,
    icon: "🔐",
    title: "Authentication Flow",
    cat: "flow",
    tagClass: "tag-flow",
    tagLabel: "App Flow",
    code: `graph TD
    START["App Starts"] --> CHK{"Has Token?"}
    CHK -->|Yes| VAL{"Is Valid?"}
    CHK -->|No| LOGIN["Login Screen"]
    
    VAL -->|Yes| HOME["Home Screen"]
    VAL -->|No| LOGIN
    
    LOGIN -->|"Credentials"| API["Auth API"]
    API -->|"JWT Token"| SAVE["Save to SecureStorage"]
    SAVE --> HOME
    
    HOME -->|"Logout"| DEL["Delete Token"]
    DEL --> LOGIN
    
    style START fill:#E8F5E9,stroke:#2E7D32
    style CHK fill:#FFF3E0,stroke:#E65100
    style LOGIN fill:#FCE4EC,stroke:#C62828`,
  },
  {
    id: 31,
    icon: "💾",
    title: "Local Data Persistence",
    cat: "data",
    tagClass: "tag-data",
    tagLabel: "Data & API",
    code: `graph LR
    subgraph FLUTTER["Flutter App"]
        REPO["Repository"]
    end
    
    subgraph LOCAL["Local Storage"]
        SP["SharedPreferences (Key-Value)"]
        HIVE["Hive (NoSQL)"]
        SQL["SQLite/sqflite (Relational)"]
    end
    
    REPO -->|"Settings/Tokens"| SP
    REPO -->|"Fast Cache/JSON"| HIVE
    REPO -->|"Complex Queries"| SQL
    
    style LOCAL fill:#EDE7F6,stroke:#4527A0`,
  },
  {
    id: 32,
    icon: "🌳",
    title: "InheritedWidget Mechanism",
    cat: "widget",
    tagClass: "tag-widget",
    tagLabel: "Widget & UI",
    code: `graph TD
    ROOT["RootApp"] --> IW["InheritedWidget (Holds Data)"]
    IW --> PARENT["ParentWidget"]
    PARENT --> C1["Child 1"]
    PARENT --> C2["Child 2"]
    
    C1 -.->|"dependOnInheritedWidgetOfExactType"| IW
    C2 -.->|"dependOnInheritedWidgetOfExactType"| IW
    
    style IW fill:#FFF3E0,stroke:#E65100`,
  },
  {
    id: 33,
    icon: "🏛️",
    title: "Clean Architecture",
    cat: "architecture",
    tagClass: "tag-architecture",
    tagLabel: "Architecture",
    code: `graph TD
    subgraph PRESENTATION["Presentation Layer"]
        UI["Widgets / UI"]
        SM["State Management (BLoC/Provider)"]
    end
    
    subgraph DOMAIN["Domain Layer (Core)"]
        UC["Use Cases"]
        ENT["Entities"]
        REP_ABS["Repository Interfaces"]
    end
    
    subgraph DATA["Data Layer"]
        REP_IMPL["Repository Implementations"]
        DS_REMOTE["Remote Data Source (API)"]
        DS_LOCAL["Local Data Source (DB)"]
    end
    
    UI --> SM
    SM --> UC
    UC --> ENT
    UC --> REP_ABS
    REP_IMPL -.->|"Implements"| REP_ABS
    REP_IMPL --> DS_REMOTE
    REP_IMPL --> DS_LOCAL
    
    style PRESENTATION fill:#E3F2FD,stroke:#1565C0
    style DOMAIN fill:#E8F5E9,stroke:#2E7D32
    style DATA fill:#FFF3E0,stroke:#E65100`,
  },
  {
    id: 34,
    icon: "💉",
    title: "Dependency Injection (GetIt)",
    cat: "architecture",
    tagClass: "tag-architecture",
    tagLabel: "Architecture",
    code: `graph TD
    INIT["setupLocator()"] --> GETIT["GetIt (Service Locator)"]
    GETIT -->|"registerSingleton"| API["ApiService"]
    GETIT -->|"registerLazySingleton"| REPO["Repository"]
    GETIT -->|"registerFactory"| BLOC["BLoC / ViewModel"]
    
    UI["UI Widget"] -.->|"GetIt.I<BLoC>()"| BLOC
    BLOC -.->|"GetIt.I<Repository>()"| REPO
    REPO -.->|"GetIt.I<ApiService>()"| API
    
    style GETIT fill:#EDE7F6,stroke:#4527A0`,
  },
  {
    id: 35,
    icon: "🛤️",
    title: "Advanced Routing (go_router)",
    cat: "navigation",
    tagClass: "tag-navigation",
    tagLabel: "Navigation",
    code: `graph TD
    APP["MaterialApp.router"] --> ROUTER["GoRouter Config"]
    
    ROUTER --> R1["/ (Home)"]
    ROUTER --> R2["/profile"]
    R2 --> R2A["/profile/settings"]
    ROUTER --> R3["/product/:id"]
    
    URL["Web URL / Deep Link"] --> ROUTER
    REDIRECT["Redirect Logic (Auth)"] -.-> ROUTER
    
    style ROUTER fill:#E8F5E9,stroke:#2E7D32
    style REDIRECT fill:#FCE4EC,stroke:#C62828`,
  },
  {
    id: 36,
    icon: "⚡",
    title: "Isolates & Multithreading",
    cat: "architecture",
    tagClass: "tag-architecture",
    tagLabel: "Architecture",
    code: `graph LR
    subgraph MAIN["Main UI Isolate"]
        M1["UI Thread"]
        M2["Event Loop"]
    end
    
    subgraph BACKGROUND["Background Isolate"]
        B1["Heavy Computation"]
        B2["JSON Parsing"]
    end
    
    MAIN -->|"Isolate.spawn() / compute()"| BACKGROUND
    BACKGROUND -->|"SendPort.send(data)"| MAIN
    MAIN -.->|"ReceivePort.listen()"| MAIN
    
    style MAIN fill:#E3F2FD,stroke:#1565C0
    style BACKGROUND fill:#FFF3E0,stroke:#E65100`,
  },
  {
    id: 37,
    icon: "📶",
    title: "Offline-First Sync",
    cat: "data",
    tagClass: "tag-data",
    tagLabel: "Data & API",
    code: `sequenceDiagram
    participant UI
    participant LocalDB as SQLite/Hive
    participant Sync as Sync Worker
    participant Cloud as Cloud API
    
    UI->>LocalDB: Read Data (Instant)
    UI->>LocalDB: Write Data
    LocalDB-->>UI: Update UI
    LocalDB->>Sync: Mark as 'pending_sync'
    loop Background
        Sync->>Cloud: Send pending data
        Cloud-->>Sync: ACK / Confirmed
        Sync->>LocalDB: Mark as 'synced'
    end`,
  },
  {
    id: 38,
    icon: "🚀",
    title: "CI/CD Pipeline",
    cat: "test",
    tagClass: "tag-test",
    tagLabel: "Testing",
    code: `graph LR
    PUSH["Git Push"] --> ACTIONS["GitHub Actions"]
    ACTIONS --> LINT["flutter analyze"]
    LINT --> TEST["flutter test"]
    TEST --> BUILD["flutter build (apk/ipa)"]
    BUILD --> FASTLANE["Fastlane"]
    
    FASTLANE --> PLAY["Google Play Store"]
    FASTLANE --> APPSTORE["Apple App Store"]
    FASTLANE --> TESTFLIGHT["TestFlight / Firebase Dist"]
    
    style ACTIONS fill:#E8F5E9,stroke:#2E7D32
    style FASTLANE fill:#FFF3E0,stroke:#E65100`,
  },
  {
    id: 39,
    icon: "💬",
    title: "WebSocket Real-time",
    cat: "data",
    tagClass: "tag-data",
    tagLabel: "Data & API",
    code: `graph TD
    APP["Flutter App"] <-->|"ws:// Full Duplex"| SERVER["WebSocket Server"]
    
    subgraph FLUTTER_SIDE["Flutter Data Flow"]
        CHAN["WebSocketChannel"] -->|"stream.listen()"| BLOC["BLoC / StreamProvider"]
        BLOC -->|"yield state"| UI["Chat UI"]
        UI -->|"sink.add(msg)"| CHAN
    end
    
    style SERVER fill:#FCE4EC,stroke:#C62828
    style CHAN fill:#E3F2FD,stroke:#1565C0`,
  },
  {
    id: 40,
    icon: "🖌️",
    title: "CustomPaint & Canvas",
    cat: "design",
    tagClass: "tag-design",
    tagLabel: "Design",
    code: `graph TD
    CP["CustomPaint Widget"] --> PAINTER["CustomPainter Class"]
    
    PAINTER --> P1["paint(Canvas, Size)"]
    PAINTER --> P2["shouldRepaint(oldDelegate)"]
    
    subgraph PAINTING["Inside paint()"]
        CANV["canvas.drawRect() / drawPath()"]
        PAINT["Paint() object (color, strokeWidth)"]
        PAINT -.-> CANV
    end
    
    style CP fill:#E8F5E9,stroke:#2E7D32
    style PAINTER fill:#FFF3E0,stroke:#E65100`,
  },
];

const CATS = [
  { key: "all", label: "🗂️ All (40)" },
  { key: "architecture", label: "🏗️ Architecture" },
  { key: "state", label: "🔄 State Management" },
  { key: "widget", label: "📱 Widget & UI" },
  { key: "navigation", label: "🧭 Navigation" },
  { key: "data", label: "🗄️ Data & API" },
  { key: "flow", label: "🔀 App Flow" },
  { key: "design", label: "🎨 Design" },
  { key: "test", label: "🧪 Testing" },
];

// ── MermaidPreview ─────────────────────────────────────────────────────────
const MermaidPreview: React.FC<{ code: string; id: number }> = ({
  code,
  id,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">(
    "idle",
  );
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (status !== "idle") {
      return;
    }
    setStatus("loading");

    import("mermaid")
      .then((mod) => {
        const mermaid = mod.default;
        mermaid.initialize({
          startOnLoad: false,
          theme: "dark",
          themeVariables: {
            background: "#111827",
            primaryColor: "#1e3a5f",
            primaryTextColor: "#e2e8f0",
            primaryBorderColor: "#0553B1",
            lineColor: "#54C5F8",
            secondaryColor: "#1a2236",
            tertiaryColor: "#0d1b35",
            nodeBorder: "#0553B1",
            clusterBkg: "#0d1b35",
            titleColor: "#54C5F8",
            edgeLabelBackground: "#111827",
            actorBkg: "#1e3a5f",
            actorBorder: "#0553B1",
            actorTextColor: "#e2e8f0",
            signalColor: "#54C5F8",
            signalTextColor: "#e2e8f0",
            noteBkgColor: "#1a2236",
            noteTextColor: "#7dd3fc",
          },
        });
        const uniqueId = `mermaid-${id}-${Date.now()}`;
        return mermaid.render(uniqueId, code);
      })
      .then(({ svg }) => {
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
          // Make SVG responsive
          const svgEl = containerRef.current.querySelector("svg");
          if (svgEl) {
            svgEl.style.maxWidth = "100%";
            svgEl.style.height = "auto";
            svgEl.removeAttribute("width");
            svgEl.removeAttribute("height");
          }
        }
        setStatus("done");
      })
      .catch((err) => {
        setErrorMsg(String(err?.message || err));
        setStatus("error");
      });
  }, [code, id, status]);

  if (status === "loading") {
    return (
      <div style={previewStyles.loadingBox}>
        <div style={previewStyles.spinner} />
        <span style={{ color: "#94a3b8", fontSize: "0.82rem" }}>
          Rendering…
        </span>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div style={previewStyles.errorBox}>
        <span style={{ fontSize: "1.2rem" }}>⚠️</span>
        <span style={{ color: "#f87171", fontSize: "0.8rem" }}>{errorMsg}</span>
      </div>
    );
  }
  return <div ref={containerRef} style={previewStyles.svgContainer} />;
};

const previewStyles = {
  loadingBox: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    minHeight: 200,
    color: "#94a3b8",
  } as React.CSSProperties,
  spinner: {
    width: 32,
    height: 32,
    border: "3px solid #1e2d45",
    borderTop: "3px solid #0553B1",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  } as React.CSSProperties,
  errorBox: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 120,
    padding: 16,
    textAlign: "center" as const,
  } as React.CSSProperties,
  svgContainer: {
    padding: "16px 12px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    minHeight: 200,
    overflowX: "auto" as const,
  } as React.CSSProperties,
};

// ── Component ──────────────────────────────────────────────────────────────
export const FlutterTemplatesPage: React.FC = () => {
  const [activeCat, setActiveCat] = useState("all");
  const [copiedId, setCopiedId] = useState<number | null>(null);
  // Track which card is in "preview" mode (card id → tab)
  const [previewTabs, setPreviewTabs] = useState<
    Record<number, "code" | "preview">
  >({});

  const getTab = (id: number) => previewTabs[id] ?? "preview";
  const setTab = (id: number, tab: "code" | "preview") =>
    setPreviewTabs((prev) => ({ ...prev, [id]: tab }));

  // Fix: Excalidraw sets overflow:hidden on body/html globally.
  // Override it while this page is mounted so the user can scroll.
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  const visible =
    activeCat === "all"
      ? TEMPLATES
      : TEMPLATES.filter((t) => t.cat === activeCat);

  const copy = (t: Template) => {
    navigator.clipboard.writeText(t.code).then(() => {
      setCopiedId(t.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div style={styles.root}>
      {/* ── Header ── */}
      <header style={styles.header}>
        <svg
          style={styles.logo}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 125"
        >
          <polygon points="10,60 40,30 55,30 25,60" fill="#54C5F8" />
          <polygon
            points="25,60 55,30 70,30 55,60 70,90 55,90"
            fill="#01579B"
          />
          <polygon points="55,60 70,90 55,90 40,75" fill="#29B6F6" />
        </svg>
        <div>
          <h1 style={styles.h1}>Flutter Dev Board — Diagram Templates</h1>
          <p style={styles.subtitle}>
            20+ Mermaid diagrams for Flutter & Dart • Offline Ready 🔌
          </p>
        </div>
        <a href="/" style={styles.backBtn}>
          ← Back to Board
        </a>
      </header>

      {/* ── How-to banner ── */}
      <div style={styles.howTo}>
        <span style={styles.step}>
          <span style={styles.stepNum}>1</span> নিচে "Copy Code" চাপুন
        </span>
        <span style={styles.arrow}>→</span>
        <span style={styles.step}>
          <span style={styles.stepNum}>2</span> Board-এ ফিরুন
        </span>
        <span style={styles.arrow}>→</span>
        <span style={styles.step}>
          <span style={styles.stepNum}>3</span> Toolbar ⊕ →{" "}
          <strong>Mermaid to Excalidraw</strong>
        </span>
        <span style={styles.arrow}>→</span>
        <span style={styles.step}>
          <span style={styles.stepNum}>4</span> Paste → Insert!
        </span>
      </div>

      {/* ── Filters ── */}
      <div style={styles.filterBar}>
        {CATS.map((c) => (
          <button
            key={c.key}
            style={{
              ...styles.filterBtn,
              ...(activeCat === c.key ? styles.filterBtnActive : {}),
            }}
            onClick={() => setActiveCat(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* ── Grid ── */}
      <div style={styles.grid}>
        {visible.map((t) => {
          const tab = getTab(t.id);
          return (
            <div key={t.id} style={styles.card}>
              {/* Card Header */}
              <div style={styles.cardHeader}>
                <span style={styles.cardIcon}>{t.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={styles.cardTitle}>{t.title}</div>
                  <span
                    style={{
                      ...styles.tag,
                      ...(styles[t.tagClass as keyof typeof styles] as any),
                    }}
                  >
                    {t.tagLabel}
                  </span>
                </div>
                {/* Code / Preview tabs */}
                <div style={styles.tabGroup}>
                  <button
                    style={{
                      ...styles.tabBtn,
                      ...(tab === "code" ? styles.tabBtnActive : {}),
                    }}
                    onClick={() => setTab(t.id, "code")}
                  >
                    📄 Code
                  </button>
                  <button
                    style={{
                      ...styles.tabBtn,
                      ...(tab === "preview" ? styles.tabBtnActive : {}),
                    }}
                    onClick={() => setTab(t.id, "preview")}
                  >
                    👁️ Preview
                  </button>
                </div>
              </div>

              {/* Code tab */}
              {tab === "code" && (
                <div style={styles.codeScroll}>
                  <pre style={styles.pre}>{t.code}</pre>
                </div>
              )}

              {/* Preview tab */}
              {tab === "preview" && (
                <div style={styles.previewPanel}>
                  <MermaidPreview code={t.code} id={t.id} />
                </div>
              )}

              {/* Footer buttons */}
              <div style={styles.cardFooter}>
                <button
                  style={{
                    ...styles.copyBtn,
                    ...(copiedId === t.id ? styles.copyBtnCopied : {}),
                  }}
                  onClick={() => copy(t)}
                >
                  {copiedId === t.id ? "✅ Copied!" : "📋 Copy Code"}
                </button>
                <button
                  style={styles.insertBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(t.code).then(() => {
                      window.location.href = "/";
                    });
                  }}
                >
                  🚀 Copy &amp; Go to Board
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  root: {
    fontFamily: "'Inter', -apple-system, sans-serif",
    background: "#0a0e1a",
    color: "#e2e8f0",
    height: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
  } as React.CSSProperties,
  header: {
    background: "linear-gradient(135deg,#0a0e1a 0%,#0d1b35 50%,#0a1628 100%)",
    borderBottom: "1px solid #1e2d45",
    padding: "22px 28px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    position: "sticky" as const,
    top: 0,
    zIndex: 100,
  } as React.CSSProperties,
  logo: { width: 44, height: 44, flexShrink: 0 } as React.CSSProperties,
  h1: {
    fontSize: "1.3rem",
    fontWeight: 700,
    background: "linear-gradient(135deg,#54C5F8,#1976D2)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    margin: 0,
    lineHeight: 1.2,
  } as React.CSSProperties,
  subtitle: {
    fontSize: "0.82rem",
    color: "#94a3b8",
    margin: "2px 0 0",
  } as React.CSSProperties,
  backBtn: {
    marginLeft: "auto",
    background: "rgba(5,83,177,0.2)",
    border: "1px solid rgba(5,83,177,0.4)",
    color: "#54C5F8",
    padding: "7px 16px",
    borderRadius: 20,
    fontSize: "0.8rem",
    fontWeight: 600,
    textDecoration: "none",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  howTo: {
    background: "linear-gradient(135deg,rgba(5,83,177,.15),rgba(0,180,171,.1))",
    border: "1px solid rgba(84,197,248,.2)",
    borderRadius: 14,
    margin: "20px 28px 0",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap" as const,
    fontSize: "0.83rem",
    color: "#94a3b8",
  } as React.CSSProperties,
  step: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  } as React.CSSProperties,
  stepNum: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#0553B1",
    color: "#fff",
    fontSize: "0.72rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as React.CSSProperties,
  arrow: { color: "#334155", fontSize: "1rem" } as React.CSSProperties,
  filterBar: {
    display: "flex",
    gap: 8,
    padding: "20px 28px 12px",
    flexWrap: "wrap" as const,
  } as React.CSSProperties,
  filterBtn: {
    background: "#111827",
    border: "1px solid #1e2d45",
    color: "#94a3b8",
    padding: "6px 16px",
    borderRadius: 20,
    fontSize: "0.8rem",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all .2s",
  } as React.CSSProperties,
  filterBtnActive: {
    background: "#0553B1",
    borderColor: "#0553B1",
    color: "#fff",
  } as React.CSSProperties,
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(460px,1fr))",
    gap: 18,
    padding: "0 28px 60px",
  } as React.CSSProperties,
  card: {
    background: "#111827",
    border: "1px solid #1e2d45",
    borderRadius: 14,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
  } as React.CSSProperties,
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 18px 12px",
    borderBottom: "1px solid #1e2d45",
  } as React.CSSProperties,
  cardIcon: {
    fontSize: "1.4rem",
    width: 38,
    height: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(5,83,177,.15)",
    borderRadius: 9,
    flexShrink: 0,
  } as React.CSSProperties,
  cardTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#e2e8f0",
    lineHeight: 1.2,
  } as React.CSSProperties,
  tag: {
    display: "inline-block",
    fontSize: "0.68rem",
    fontWeight: 500,
    padding: "2px 8px",
    borderRadius: 10,
    marginTop: 4,
  } as React.CSSProperties,
  "tag-arch": {
    background: "rgba(103,80,164,.25)",
    color: "#c4b5fd",
  } as React.CSSProperties,
  "tag-state": {
    background: "rgba(5,83,177,.25)",
    color: "#93c5fd",
  } as React.CSSProperties,
  "tag-nav": {
    background: "rgba(34,197,94,.2)",
    color: "#86efac",
  } as React.CSSProperties,
  "tag-widget": {
    background: "rgba(245,158,11,.2)",
    color: "#fcd34d",
  } as React.CSSProperties,
  "tag-data": {
    background: "rgba(0,180,171,.2)",
    color: "#5eead4",
  } as React.CSSProperties,
  "tag-design": {
    background: "rgba(236,72,153,.2)",
    color: "#f9a8d4",
  } as React.CSSProperties,
  "tag-test": {
    background: "rgba(34,197,94,.15)",
    color: "#6ee7b7",
  } as React.CSSProperties,
  "tag-flow": {
    background: "rgba(251,146,60,.2)",
    color: "#fdba74",
  } as React.CSSProperties,
  codeScroll: {
    flex: 1,
    overflowX: "auto" as const,
    overflowY: "auto" as const,
    maxHeight: 240,
    padding: "14px 18px",
    scrollbarWidth: "thin" as const,
  } as React.CSSProperties,
  pre: {
    fontFamily: "'JetBrains Mono','Fira Code',monospace",
    fontSize: "0.73rem",
    lineHeight: 1.6,
    color: "#7dd3fc",
    whiteSpace: "pre" as const,
    margin: 0,
  } as React.CSSProperties,
  copyBtn: {
    flex: 1,
    background: "linear-gradient(135deg,#0553B1,#1976D2)",
    border: "none",
    color: "#fff",
    padding: "11px 14px",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.02em",
    transition: "all .2s",
    borderRadius: "0 0 0 13px",
  } as React.CSSProperties,
  copyBtnCopied: {
    background: "linear-gradient(135deg,#16a34a,#22c55e)",
  } as React.CSSProperties,
  // ── Tab styles ──
  tabGroup: {
    display: "flex",
    gap: 4,
    marginLeft: "auto",
    flexShrink: 0,
  } as React.CSSProperties,
  tabBtn: {
    background: "transparent",
    border: "1px solid #1e2d45",
    color: "#94a3b8",
    padding: "4px 10px",
    borderRadius: 8,
    fontSize: "0.75rem",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all .15s",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  tabBtnActive: {
    background: "#0553B1",
    borderColor: "#0553B1",
    color: "#fff",
  } as React.CSSProperties,
  // ── Preview panel ──
  previewPanel: {
    flex: 1,
    minHeight: 240,
    maxHeight: 340,
    overflowY: "auto" as const,
    overflowX: "auto" as const,
    background: "#0d1625",
    borderBottom: "1px solid #1e2d45",
    scrollbarWidth: "thin" as const,
  } as React.CSSProperties,
  // ── Footer ──
  cardFooter: {
    display: "flex",
    gap: 0,
  } as React.CSSProperties,
  insertBtn: {
    flex: 1.2,
    background: "linear-gradient(135deg,#00695C,#00897B)",
    border: "none",
    color: "#fff",
    padding: "11px 10px",
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.02em",
    transition: "all .2s",
    borderLeft: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "0 0 13px 0",
  } as React.CSSProperties,
};

// Inject spinner keyframe animation into document
if (typeof document !== "undefined") {
  const styleId = "flutter-templates-spinner";
  if (!document.getElementById(styleId)) {
    const styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleEl);
  }
}
